import { Checkbox, Col, Form, type FormInstance, Input, type InputRef, Modal, Row } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';
import type { ProdutoAdicionalBody } from '@/features/produto-adicional/types/ProdutoAdicional';
import type { ProdutoAdicionalItemBody } from '@/features/produto-adicional/types/ProdutoAdicionalItem';
import type { FormProdutoValues } from '@/features/produto/types/FormProdutoValues';
import type { Dialog } from '@/std/dialog/types';
import { PickDateTime } from '@/components/date/PickDateTime';
import { UploadImage } from '@/components/image/upload-image/UploadImage';
import { InputNumber } from '@/components/input/InputNumber';
import {
    ProdutoAdicionalItemStatus,
    ProdutoAdicionalTipoSelecao,
} from '@/features/produto-adicional/types/enum';
import { DATE_TIME_FORMAT_API } from '@/std/const/date';
import { uuid } from '@/std/util';
import { addOrEditAdicionalItemForm } from './util';

export function ProdutoTabAdicionalModal({
    adicional,
    formProduto,
    dialog,
    onClose,
    setFormDirty,
}: {
    adicional: ProdutoAdicionalBody | null;
    formProduto: FormInstance<FormProdutoValues>;
    dialog: Dialog<ProdutoAdicionalItemBody>;
    onClose: () => void;
    setFormDirty: (dirty: boolean) => void;
}) {
    const descricaoRef = useRef<InputRef>(null);

    const [formAdicional] = Form.useForm<ProdutoAdicionalItemBody>();

    const title = `${dialog.state.mode === 'add' ? 'Adicionar' : 'Editar'} item no grupo "${adicional?.pra_nome || ''}"`;

    const adicionalUnico = adicional?.pra_tipo_selecao === ProdutoAdicionalTipoSelecao.Unica;

    const pai_imagem = Form.useWatch('pai_imagem', formAdicional);

    const onFinish = (values: ProdutoAdicionalItemBody): void => {
        if (!adicional) {
            return;
        }

        addOrEditAdicionalItemForm(formProduto, dialog.state, adicional, values);
        setFormDirty(true);
        formAdicional.resetFields();
        onClose();
    };

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        if (dialog.state.open) {
            formAdicional.resetFields();

            if (dialog.state.mode === 'edit' && dialog.state.data) {
                formAdicional.setFieldsValue(dialog.state.data);
            } else {
                formAdicional.setFieldsValue({
                    key: uuid(),
                    pai_aceita_multiplos: 'N',
                    pai_data_insercao: dayjs().format(DATE_TIME_FORMAT_API),
                    pai_status: ProdutoAdicionalItemStatus.Ativo,
                });
            }

            timeoutId = setTimeout(() => {
                if (descricaoRef.current && dialog.state.open) {
                    descricaoRef.current.focus();
                }
            }, 300);
        }

        return () => clearTimeout(timeoutId);
    }, [dialog.state, formAdicional]);

    return (
        <Modal
            onCancel={onClose}
            onOk={() => formAdicional.submit()}
            open={dialog.state.open}
            title={title}
        >
            <Form form={formAdicional} layout='vertical' onFinish={onFinish}>
                <Form.Item hidden name='key' />
                <Form.Item hidden name='pai_idpk' />

                <Row gutter={[8, 8]}>
                    <Col xs={24}>
                        <Form.Item hidden={true} name='pai_imagem' />
                        <Form.Item hidden={true} name='imagem_nome' />
                        <Form.Item hidden={true} name='imagem_base64' />

                        <p>Imagem</p>

                        <UploadImage
                            onChange={({ imagem_base64, imagem_nome }) => {
                                formAdicional.setFieldsValue({
                                    imagem_base64,
                                    imagem_nome,
                                    pai_imagem: `data:image/png;base64,${imagem_base64}`,
                                });
                            }}
                            src={pai_imagem}
                        />
                    </Col>

                    <Col sm={10} xs={14}>
                        <Form.Item label='Inserção' name='pai_data_insercao'>
                            <PickDateTime disabled={true} />
                        </Form.Item>
                    </Col>

                    <Col sm={7} xs={12}>
                        <Form.Item
                            getValueFromEvent={(e) =>
                                e.target.checked
                                    ? ProdutoAdicionalItemStatus.Ativo
                                    : ProdutoAdicionalItemStatus.Inativo
                            }
                            getValueProps={(value) => ({
                                checked: value === ProdutoAdicionalItemStatus.Ativo,
                            })}
                            label='Status'
                            name='pai_status'
                            valuePropName='checked'
                        >
                            <Checkbox>Ativo</Checkbox>
                        </Form.Item>
                    </Col>

                    <Col sm={7} xs={12}>
                        <Form.Item
                            getValueFromEvent={(e) => (e.target.checked ? 'S' : 'N')}
                            getValueProps={(value) => ({
                                checked: value === 'S',
                            })}
                            help={
                                adicionalUnico
                                    ? 'Este grupo de adicionais não aceita múltiplos itens.'
                                    : ''
                            }
                            label='Aceita múltiplos'
                            name='pai_aceita_multiplos'
                            tooltip='Caso o grupo de adicionais aceitar múltiplos itens, este campo ficará disponível para ser informado.'
                            valuePropName='checked'
                        >
                            <Checkbox disabled={adicionalUnico}>Sim</Checkbox>
                        </Form.Item>
                    </Col>

                    <Col xs={24}>
                        <Form.Item
                            label='Descrição'
                            name='pai_nome'
                            rules={[{ message: 'Campo obrigatório', required: true }]}
                        >
                            <Input className='w-full' ref={descricaoRef} />
                        </Form.Item>
                    </Col>

                    <Col xs={24}>
                        <Form.Item
                            label='Detalhes'
                            name='pai_detalhes'
                            tooltip='Informe tudo que compõem este adicional para o comprador estar ciente.'
                        >
                            <Input.TextArea className='w-full' />
                        </Form.Item>
                    </Col>

                    <Col xs={12}>
                        <Form.Item label='Valor unitário' name='pai_valor_unitario'>
                            <InputNumber decimalScale={2} type='$' />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
}
